import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FiPhone, FiMapPin, FiTruck, FiCheckCircle,
  FiDollarSign, FiShield, FiClock, FiArrowRight
} from 'react-icons/fi';
import { supabase } from '../../../lib/supabase';
import { getVehicleUrl } from '../../../utils/vehicleSlug';

/**
 * Reusable Location Vehicle Sales Landing Page
 *
 * @param {Object} config - City-specific configuration
 * @param {string} config.city - City name (e.g. "Decatur")
 * @param {string} config.state - State abbreviation (e.g. "TX")
 * @param {string} config.slug - URL slug (e.g. "decatur-tx")
 * @param {string} config.county - County name (e.g. "Wise County")
 * @param {string} config.driveTime - Drive time from city to Paradise
 * @param {string} config.directions - Driving directions text
 * @param {string[]} config.nearbyAreas - Array of nearby town names
 * @param {Object} config.uniqueContent - City-specific content sections
 * @param {string} config.uniqueContent.heroSubtitle - Hero subtitle text
 * @param {string} config.uniqueContent.whyBuyIntro - Intro paragraph
 * @param {string[]} config.uniqueContent.buyerBenefits - List of benefits
 * @param {string[]} config.uniqueContent.contentSections - Array of {title, body} objects
 * @param {Object[]} config.faqs - Array of {q, a} FAQ objects
 * @param {string} config.metaDescription - Unique meta description
 * @param {string} config.ogDescription - Unique OG description
 * @param {string} config.title - Full title tag
 * @param {string} config.h1 - H1 text (must differ from title)
 */
const LocationVehiclePage = ({ config }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const canonicalUrl = `https://paradise-performance.com/used-cars-${config.slug}`;

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const { data, error } = await supabase
        .from('vehicles_db2025')
        .select('*')
        .eq('status', 'available')
        .order('created_at', { ascending: false })
        .limit(12);

      if (error) throw error;
      setVehicles(data || []);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'Call for Price';
    return `$${Number(price).toLocaleString()}`;
  };

  const getImageUrl = (vehicle) => {
    if (vehicle.images && vehicle.images.length > 0) {
      return vehicle.images[0]?.url || '/assets/placeholder-vehicle.jpg';
    }
    return '/assets/placeholder-vehicle.jpg';
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen">
      <Helmet>
        <title>{config.title}</title>
        <meta name="description" content={config.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={`Used Cars & Trucks Near ${config.city} - Paradise-Performance`} />
        <meta property="og:description" content={config.ogDescription} />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={`Shop Used Vehicles in ${config.city}, ${config.state}`} />
        <meta property="twitter:description" content={config.ogDescription} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoDealer",
            "name": "Paradise-Performance",
            "description": `Quality used cars, trucks, golf carts and trailers serving ${config.city}, ${config.state}`,
            "url": canonicalUrl,
            "telephone": "+18173822316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 33.15,
              "longitude": -97.6836
            },
            "areaServed": [
              {
                "@type": "City",
                "name": config.city,
                "containedInPlace": {
                  "@type": "State",
                  "name": "Texas"
                }
              },
              ...config.nearbyAreas.slice(0, 5).map(area => ({
                "@type": "City",
                "name": area
              }))
            ],
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "09:00",
                "closes": "18:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Saturday",
                "opens": "10:00",
                "closes": "14:00"
              }
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": `Used Vehicles for ${config.city} Buyers`,
              "itemListElement": vehicles.slice(0, 6).map(v => ({
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Car",
                  "name": `${v.year} ${v.make} ${v.model}`,
                  "manufacturer": v.make,
                  "model": v.model,
                  "vehicleModelDate": String(v.year),
                  "mileageFromOdometer": {
                    "@type": "QuantitativeValue",
                    "value": v.mileage,
                    "unitCode": "SMI"
                  }
                },
                "price": v.price,
                "priceCurrency": "USD"
              }))
            }
          })}
        </script>

        {/* BreadcrumbList schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://paradise-performance.com/" },
              { "@type": "ListItem", "position": 2, "name": "Inventory", "item": "https://paradise-performance.com/inventory" },
              { "@type": "ListItem", "position": 3, "name": `Used Cars ${config.city}`, "item": canonicalUrl }
            ]
          })}
        </script>

        {/* FAQPage schema */}
        {config.faqs && config.faqs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": config.faqs.map(faq => ({
                "@type": "Question",
                "name": faq.q,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.a
                }
              }))
            })}
          </script>
        )}
      </Helmet>

      {/* ─── Breadcrumb ─── */}
      <nav className="bg-gray-100 py-3 px-4 text-sm text-gray-600">
        <div className="container mx-auto">
          <Link to="/" className="hover:text-primary-600">Home</Link>
          <span className="mx-2">›</span>
          <Link to="/inventory" className="hover:text-primary-600">Inventory</Link>
          <span className="mx-2">›</span>
          <span className="text-gray-900 font-medium">Used Cars {config.city}</span>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 75% 50%, rgba(217,119,6,0.15) 0%, transparent 50%)'
          }} />
        </div>

        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl">
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
            >
              {config.h1}
            </motion.h1>

            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-xl md:text-2xl mb-8 text-gray-300 max-w-3xl"
            >
              {config.uniqueContent.heroSubtitle}
            </motion.p>

            {/* Value props */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
                <FiDollarSign className="text-3xl mb-3 text-amber-400" />
                <h3 className="font-bold text-lg">Below Market Prices</h3>
                <p className="text-sm text-gray-300">Lower overhead = lower prices for {config.city} buyers</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
                <FiShield className="text-3xl mb-3 text-green-400" />
                <h3 className="font-bold text-lg">Warranty Included</h3>
                <p className="text-sm text-gray-300">90-day / 3,000-mile powertrain warranty</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10">
                <FiTruck className="text-3xl mb-3 text-blue-400" />
                <h3 className="font-bold text-lg">Full Inventory</h3>
                <p className="text-sm text-gray-300">Cars, trucks, golf carts and trailers</p>
              </div>
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/inventory"
                className="inline-flex items-center justify-center px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-lg transition-colors"
              >
                View All Inventory <FiArrowRight className="ml-2" />
              </Link>
              <a
                href="tel:+18173822316"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white rounded-lg font-semibold text-lg hover:bg-white/20 transition-colors"
              >
                <FiPhone className="mr-2" /> (817) 382-2316
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── Areas We Serve ─── */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-10">
            Serving {config.city} and {config.county}
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 max-w-5xl mx-auto">
            {config.nearbyAreas.map((area, i) => (
              <motion.div
                key={area}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3, delay: i * 0.03 }}
                className="bg-white rounded-lg shadow-sm p-4 text-center hover:shadow-md transition-shadow border border-gray-100"
              >
                <FiMapPin className="inline text-amber-500 mb-1" />
                <span className="block font-medium text-sm">{area}</span>
              </motion.div>
            ))}
          </div>

          <p className="text-center mt-8 text-gray-600 max-w-2xl mx-auto">
            Located in Paradise, TX — just <strong>{config.driveTime}</strong> from {config.city}.{' '}
            {config.directions}
          </p>
        </div>
      </section>

      {/* ─── Featured Inventory ─── */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-3">
            Vehicles Available for {config.city} Buyers
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Browse our current inventory. Every vehicle includes a warranty and thorough inspection.
          </p>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
              <p className="mt-4 text-gray-500">Loading inventory...</p>
            </div>
          ) : vehicles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">No vehicles currently listed. Check back soon or give us a call.</p>
              <a href="tel:+18173822316" className="text-amber-600 font-semibold hover:underline">
                (817) 382-2316
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.slice(0, 9).map((v, i) => (
                <motion.div
                  key={v.id}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow border border-gray-100"
                >
                  <div className="h-48 bg-gray-200 overflow-hidden">
                    <img
                      src={getImageUrl(v)}
                      alt={`${v.year} ${v.make} ${v.model} for sale near ${config.city}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      {v.year} {v.make} {v.model}
                    </h3>
                    <p className="text-2xl font-bold text-amber-600 mb-2">
                      {formatPrice(v.price)}
                    </p>
                    {v.mileage && (
                      <p className="text-sm text-gray-500 mb-4">
                        {Number(v.mileage).toLocaleString()} miles
                      </p>
                    )}
                    <Link
                      to={getVehicleUrl(v)}
                      className="block w-full text-center bg-gray-900 text-white py-2.5 rounded-lg hover:bg-amber-600 transition-colors font-medium"
                    >
                      View Details
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <Link
              to="/inventory"
              className="inline-flex items-center px-8 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors"
            >
              See Full Inventory <FiArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Why Buy From Us ─── */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-10">
            Why {config.city} Residents Choose Paradise-Performance
          </h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl shadow-md p-8">
              <h3 className="text-xl font-bold mb-4">Better Deals Than {config.city} Lots</h3>
              <ul className="space-y-3">
                {config.uniqueContent.buyerBenefits.map((benefit, i) => (
                  <li key={i} className="flex items-start">
                    <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8">
              <h3 className="text-xl font-bold mb-4">Easy From {config.city}</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Only {config.driveTime} from {config.city}</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Trade-ins accepted from all {config.county} dealers</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Financing for {config.city} area residents</span>
                </li>
                <li className="flex items-start">
                  <FiCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">On-site service center for every vehicle sold</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Unique City Content ─── */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg prose-gray">
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              {config.uniqueContent.whyBuyIntro}
            </p>

            {config.uniqueContent.contentSections.map((section, i) => (
              <div key={i} className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">{section.title}</h2>
                <p className="text-gray-700 leading-relaxed">{section.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ Section ─── */}
      {config.faqs && config.faqs.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-10">
              Frequently Asked Questions — {config.city} Buyers
            </h2>

            <div className="max-w-3xl mx-auto space-y-4">
              {config.faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ y: 15, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.q}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── CTA Section ─── */}
      <section className="bg-gradient-to-r from-amber-600 to-amber-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Find Your Next Vehicle?
          </h2>
          <p className="text-xl mb-8 text-amber-100 max-w-2xl mx-auto">
            {config.city} residents save thousands. Financing available for all credit situations. Trade-ins welcome.
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-3xl mx-auto border border-white/20">
            <h3 className="text-2xl font-bold mb-6">Visit Us From {config.city}</h3>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <Link
                to="/inventory"
                className="inline-flex items-center justify-center px-6 py-4 bg-white text-amber-700 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
              >
                Browse Inventory
              </Link>
              <a
                href="tel:+18173822316"
                className="inline-flex items-center justify-center px-6 py-4 border-2 border-white text-white rounded-lg font-semibold text-lg hover:bg-white/10 transition-colors"
              >
                <FiPhone className="mr-2" /> (817) 382-2316
              </a>
            </div>

            <div className="text-white/90 text-sm space-y-2">
              <p className="flex items-center justify-center">
                <FiMapPin className="mr-2" /> 305 Co Rd 3470, Paradise, TX 76073
              </p>
              <p>{config.driveTime} from {config.city} — {config.directions}</p>
              <p>
                <FiClock className="inline mr-2" />
                Mon-Fri 9AM-5PM • Sat 10AM-2PM (Sales Only)
              </p>
            </div>
          </div>

          {/* Cross-links to other location pages */}
          <div className="mt-12 max-w-4xl mx-auto">
            <p className="text-amber-200 text-sm mb-3">Also serving:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {['Decatur', 'Bridgeport', 'Weatherford', 'Denton', 'Fort Worth', 'Wise County'].map(area => {
                const areaSlug = area.toLowerCase().replace(/\s+/g, '-');
                const url = area === 'Wise County'
                  ? '/used-cars-wise-county'
                  : area === 'Fort Worth'
                    ? '/used-cars-fort-worth'
                    : `/used-cars-${areaSlug}-tx`;
                // Don't link to self
                if (area === config.city) return null;
                return (
                  <Link
                    key={area}
                    to={url}
                    className="text-sm text-white/80 hover:text-white bg-white/10 px-3 py-1 rounded-full transition-colors"
                  >
                    Used Cars {area}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default LocationVehiclePage;
